// src/pages/Financial/Invoices.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  Grid,
  Card,
  CardContent,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Payment as PaymentIcon,
  Receipt as InvoiceIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import apiClient from '../../api/client';
import { formatCurrency as formatCurrencyUtil } from '../../utils/currency';
import DevOnly from '../../components/DevOnly';

const InvoicesManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // Fetch invoices data
  const { data: invoicesData, isLoading, error } = useQuery({
    queryKey: ['invoices', searchTerm, statusFilter, dateRange, paginationModel],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        params.append('skip', String(paginationModel.page * paginationModel.pageSize));
        params.append('limit', String(paginationModel.pageSize));
        if (searchTerm) params.append('search', searchTerm);
        if (statusFilter !== 'all') params.append('status', statusFilter);
        if (dateRange !== 'all') params.append('date_range', dateRange);
        
        const response = await apiClient.get(`/financial/invoices?${params.toString()}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching invoices:', error);
        return { invoices: [], total: 0, stats: {} };
      }
    },
    staleTime: 2 * 60 * 1000,
  });

  const formatCurrency = (amount) => formatCurrencyUtil(amount);

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'draft':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const statuses = ['all', 'draft', 'pending', 'paid', 'overdue'];
    setStatusFilter(statuses[newValue]);
  };

  const columns = [
    {
      field: 'invoice_number',
      headerName: 'Invoice #',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value || `INV-${params.row.id?.slice(-6)}`}
        </Typography>
      )
    },
    {
      field: 'client_name',
      headerName: 'Client',
      width: 200,
      renderCell: (params) => params.value || 'Unknown Client'
    },
    {
      field: 'invoice_date',
      headerName: 'Date',
      width: 120,
      renderCell: (params) => formatDate(params.value || params.row.created_at)
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      width: 120,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: 'total_amount',
      headerName: 'Total',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value || 'Pending'}
          color={getStatusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => navigate(`/financial/invoices/${params.row.id}`)}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <DevOnly>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => navigate(`/financial/invoices/${params.row.id}/edit`)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </DevOnly>
          <Tooltip title="Email">
            <IconButton
              size="small"
              onClick={() => console.log('Email invoice:', params.row.id)}
            >
              <EmailIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton
              size="small"
              onClick={() => console.log('Download invoice:', params.row.id)}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status !== 'paid' && (
            <DevOnly>
              <Tooltip title="Record Payment">
                <IconButton
                  size="small"
                  onClick={() => navigate(`/financial/payments/record?invoice=${params.row.id}`)}
                >
                  <PaymentIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </DevOnly>
          )}
        </Box>
      )
    }
  ];

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load invoices. Please try again later.
        </Alert>
      </Box>
    );
  }

  const stats = invoicesData?.stats || {
    total_invoices: 0,
    total_amount: 0,
    paid_amount: 0,
    outstanding_amount: 0
  };
  
  // Use the total from backend for accurate count
  const totalInvoices = invoicesData?.total || 0;

  const invoices = invoicesData?.invoices || [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Invoices
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create, manage, and track client invoices
          </Typography>
        </Box>
        <DevOnly>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/financial/invoices/create')}
            size="large"
          >
            Create Invoice
          </Button>
        </DevOnly>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Invoices
              </Typography>
              <Typography variant="h5" component="div">
                {totalInvoices}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(stats.total_amount)} total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Paid
              </Typography>
              <Typography variant="h5" component="div" color="success.main">
                {stats.paid || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(stats.paid_amount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Outstanding
              </Typography>
              <Typography variant="h5" component="div" color="warning.main">
                {stats.pending || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(stats.outstanding_amount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Overdue
              </Typography>
              <Typography variant="h5" component="div" color="error.main">
                {stats.overdue}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Requires attention
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={`All (${totalInvoices})`} />
            <Tab label={`Draft (${stats.draft || 0})`} />
            <Tab label={`Pending (${stats.pending || 0})`} />
            <Tab label={`Paid (${stats.paid || 0})`} />
            <Tab label={`Overdue (${stats.overdue || 0})`} />
          </Tabs>
        </Box>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search by invoice number, client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, maxWidth: 400 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="this_week">This Week</MenuItem>
              <MenuItem value="this_month">This Month</MenuItem>
              <MenuItem value="last_month">Last Month</MenuItem>
              <MenuItem value="this_year">This Year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
          >
            More Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            Export
          </Button>
        </Box>
      </Paper>

      {/* Invoices DataGrid */}
      <Paper>
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={invoices}
            columns={columns}
            rowCount={invoicesData?.total || 0}
            pageSizeOptions={[25, 50, 100]}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            checkboxSelection
            disableSelectionOnClick
            loading={isLoading}
            getRowId={(row) => row.id}
            sx={{
              '& .MuiDataGrid-root': {
                border: 'none',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'grey.50',
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default InvoicesManagement;